import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { adminApi, Template } from '../../api/client';
import { useToast } from '../../contexts/ToastContext';
import { SkeletonTable } from '../../components/SkeletonTable';
import { EmptyState } from '../../components/EmptyState';
import { InfoTooltip } from '../../components/InfoTooltip';

export function AdminTemplatesPage() {
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  const [searchParams] = useSearchParams();
  const [selectedTenantId, setSelectedTenantId] = useState<string>('_global');
  const [filterIndustry, setFilterIndustry] = useState('');
  const [filterCompany, setFilterCompany] = useState('');
  const [filterLabelType, setFilterLabelType] = useState('');

  // Auto-populate company filter from URL query param (e.g. ?company=walmart)
  useEffect(() => {
    const companyParam = searchParams.get('company');
    if (companyParam) {
      setFilterCompany(companyParam);
    }
  }, [searchParams]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState<{ type: 'approve' | 'activate'; key: string; version: string } | null>(null);

  const { data: tenants } = useQuery({
    queryKey: ['admin', 'tenants'],
    queryFn: () => adminApi.getTenants(),
  });

  const { data: templates, isLoading } = useQuery({
    queryKey: ['admin', 'templates', selectedTenantId, filterIndustry, filterCompany, filterLabelType, searchQuery],
    queryFn: () => adminApi.getTemplates({
      tenantId: selectedTenantId,
      industry: filterIndustry || undefined,
      company: filterCompany || undefined,
      labelType: filterLabelType || undefined,
      search: searchQuery || undefined,
    }),
  });

  // Fetch template detail (including ZPL content) when a template is selected
  const { data: templateDetail } = useQuery({
    queryKey: ['admin', 'template-detail', selectedTemplate?.templateKey],
    queryFn: () => selectedTemplate ? adminApi.getTemplate(encodeURIComponent(selectedTemplate.templateKey)) : Promise.resolve(null),
    enabled: !!selectedTemplate,
  });

  // Merge list data with detail data (ZPL content)
  const enrichedTemplate = selectedTemplate ? {
    ...selectedTemplate,
    zplContent: templateDetail?.zplContent ?? selectedTemplate.zplContent,
  } : null;

  const createTemplateMutation = useMutation({
    mutationFn: (data: {
      tenantId: string;
      industry: string;
      company: string;
      templateName: string;
      labelType: string;
      description?: string;
      zplContent: string;
      width: number;
      height: number;
      dpi: number;
    }) => adminApi.createTemplate({
      tenantId: data.tenantId,
      industry: data.industry,
      company: data.company,
      templateName: data.templateName,
      labelType: data.labelType,
      description: data.description,
      zplContent: data.zplContent,
      width: data.width,
      height: data.height,
      dpi: data.dpi,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'templates'] });
      setShowCreateModal(false);
      addToast({ type: 'success', message: 'Template created successfully' });
    },
    onError: (error) => {
      addToast({ type: 'error', message: `Failed to create template: ${error instanceof Error ? error.message : 'Unknown error'}` });
    },
  });

  const assignTemplateMutation = useMutation({
    mutationFn: (data: { tenantId: string; templateId: string; labelType: string; priority: number }) =>
      adminApi.assignTemplate(data.templateId, data.tenantId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'templates'] });
      setShowAssignModal(false);
      addToast({ type: 'success', message: 'Template assigned successfully' });
    },
    onError: (error) => {
      addToast({ type: 'error', message: `Failed to assign template: ${error instanceof Error ? error.message : 'Unknown error'}` });
    },
  });

  const approveTemplateMutation = useMutation({
    mutationFn: ({ key, version }: { key: string; version: string }) =>
      adminApi.approveTemplate(key, version),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'templates'] });
      setConfirmAction(null);
      if (selectedTemplate) {
        setSelectedTemplate({ ...selectedTemplate, status: 'approved' });
      }
      addToast({ type: 'success', message: 'Template approved successfully' });
    },
    onError: (error) => {
      addToast({ type: 'error', message: `Failed to approve template: ${error instanceof Error ? error.message : 'Unknown error'}` });
    },
  });

  const activateTemplateMutation = useMutation({
    mutationFn: ({ key, version }: { key: string; version: string }) =>
      adminApi.activateTemplate(key, version),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'templates'] });
      setConfirmAction(null);
      if (selectedTemplate) {
        setSelectedTemplate({ ...selectedTemplate, status: 'active' });
      }
      addToast({ type: 'success', message: 'Template activated — now available for label generation' });
    },
    onError: (error) => {
      addToast({ type: 'error', message: `Failed to activate template: ${error instanceof Error ? error.message : 'Unknown error'}` });
    },
  });

  const handleCreateTemplate = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    createTemplateMutation.mutate({
      tenantId: formData.get('tenantId') as string || '_global',
      industry: formData.get('industry') as string,
      company: formData.get('company') as string,
      templateName: formData.get('templateName') as string,
      labelType: formData.get('labelType') as string,
      description: (formData.get('description') as string) || undefined,
      zplContent: formData.get('zplContent') as string,
      width: parseFloat(formData.get('width') as string),
      height: parseFloat(formData.get('height') as string),
      dpi: parseInt(formData.get('dpi') as string) || 203,
    });
  };

  const handleAssignTemplate = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!selectedTemplate) return;
    const formData = new FormData(e.currentTarget);
    assignTemplateMutation.mutate({
      tenantId: formData.get('assignTenantId') as string,
      templateId: selectedTemplate.templateKey,
      labelType: formData.get('assignLabelType') as string,
      priority: parseInt(formData.get('priority') as string) || 0,
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div className="h-8 w-32 bg-gray-200 rounded animate-pulse" />
          <div className="h-10 w-40 bg-gray-200 rounded animate-pulse" />
        </div>
        <div className="card"><SkeletonTable rows={6} columns={4} /></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Templates</h1>
        <button
          className="btn btn-primary"
          onClick={() => setShowCreateModal(true)}
        >
          + Create Template
        </button>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="label">Tenant</label>
            <select
              className="input w-64"
              value={selectedTenantId}
              onChange={(e) => setSelectedTenantId(e.target.value)}
            >
              <option value="_global">Global Templates</option>
              {tenants?.map((tenant) => (
                <option key={tenant.tenantId} value={tenant.tenantId}>
                  {tenant.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Industry</label>
            <input
              className="input w-44"
              placeholder="travel-center"
              value={filterIndustry}
              onChange={(e) => setFilterIndustry(e.target.value)}
            />
          </div>
          <div>
            <label className="label">Company</label>
            <input
              className="input w-44"
              placeholder="buc-ees"
              value={filterCompany}
              onChange={(e) => setFilterCompany(e.target.value)}
            />
          </div>
          <div>
            <label className="label">Label Type</label>
            <select
              className="input w-40"
              value={filterLabelType}
              onChange={(e) => setFilterLabelType(e.target.value)}
            >
              <option value="">All</option>
              <option value="shipping">Shipping</option>
              <option value="carton">Carton</option>
              <option value="pallet">Pallet</option>
              <option value="box">Box</option>
              <option value="custom">Custom</option>
            </select>
          </div>
          <div className="flex-1 min-w-[220px]">
            <label className="label">Search</label>
            <input
              className="input"
              placeholder="Search industry, company, name, key..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Template List */}
        <div className="lg:col-span-2 card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Templates</h2>
          <div className="space-y-3">
            {templates?.map((template) => (
              <div
                key={`${template.templateKey}_v${template.version}`}
                className={`p-4 border rounded cursor-pointer hover:border-primary-300 ${
                  selectedTemplate?.templateKey === template.templateKey && selectedTemplate?.version === template.version
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-gray-200'
                }`}
                onClick={() => setSelectedTemplate(template)}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-medium">{template.templateName || template.templateKey}</h3>
                    <p className="text-sm text-gray-500">
                      {(template.industry || 'unknown')} • {(template.company || template.retailer)} • {template.labelType} • v{template.version}
                    </p>
                  </div>
                  <div className="text-right">
                    <span className={`px-2 py-1 text-xs rounded ${
                      template.status === 'active'
                        ? 'bg-green-100 text-green-800'
                        : template.status === 'approved'
                          ? 'bg-blue-100 text-blue-800'
                          : template.status === 'draft'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-gray-100 text-gray-800'
                    }`}>
                      {template.status}
                    </span>
                    <p className="text-xs text-gray-500 mt-1">
                      {template.width || 4}" × {template.height || 6}"
                    </p>
                  </div>
                </div>
              </div>
            ))}
            {(!templates || templates.length === 0) && (
              <EmptyState
                icon="📄"
                title="No templates found"
                description="Create a template or adjust filters"
                actionLabel="+ Create Template"
                onAction={() => setShowCreateModal(true)}
              />
            )}
          </div>
        </div>

        {/* Template Details */}
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Details</h2>
          {enrichedTemplate ? (
            <div className="space-y-4">
              <div>
                <label className="label">Template ID</label>
                <p className="font-mono text-xs bg-gray-50 p-2 rounded break-all">
                  {enrichedTemplate.templateKey}
                </p>
              </div>
              <div>
                <label className="label">Template Name</label>
                <p>{enrichedTemplate.templateName || enrichedTemplate.templateKey}</p>
              </div>
              <div>
                <label className="label">Industry</label>
                <p>{enrichedTemplate.industry || 'unknown'}</p>
              </div>
              <div>
                <label className="label">Company</label>
                <p>{enrichedTemplate.company || enrichedTemplate.retailer}</p>
              </div>
              <div>
                <label className="label">Label Type</label>
                <p>{enrichedTemplate.labelType}</p>
              </div>
              <div>
                <label className="label">Dimensions</label>
                <p>{enrichedTemplate.width || 4}" × {enrichedTemplate.height || 6}" @ {enrichedTemplate.dpi || 203} DPI</p>
              </div>
              <div>
                <label className="label">Version</label>
                <p>{enrichedTemplate.version}</p>
              </div>
              <div>
                <label className="label">Created</label>
                <p>{new Date(enrichedTemplate.createdAt).toLocaleString()}</p>
              </div>
              <div>
                <label className="label">ZPL Preview</label>
                <pre className="text-xs bg-gray-50 p-2 rounded overflow-auto max-h-48">
                  {enrichedTemplate.zplContent?.substring(0, 500) || 'Loading ZPL...'}
                  {(enrichedTemplate.zplContent?.length || 0) > 500 && '...'}
                </pre>
              </div>
              <div className="pt-4 border-t space-y-2">
                {/* Workflow actions */}
                {enrichedTemplate.status === 'draft' && (
                  <button
                    className="w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                    onClick={() => setConfirmAction({ type: 'approve', key: enrichedTemplate.templateKey, version: String(enrichedTemplate.version) })}
                  >
                    Approve Version {enrichedTemplate.version}
                  </button>
                )}
                {enrichedTemplate.status === 'approved' && (
                  <button
                    className="w-full px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700"
                    onClick={() => setConfirmAction({ type: 'activate', key: enrichedTemplate.templateKey, version: String(enrichedTemplate.version) })}
                  >
                    Activate Version {enrichedTemplate.version}
                  </button>
                )}
                <button
                  className="btn btn-primary w-full"
                  onClick={() => setShowAssignModal(true)}
                >
                  Assign to Tenant
                </button>
                <p className="text-xs text-gray-500 mt-1">
                  A template must be assigned to a tenant before they can use it. Tenants only see their assigned templates.
                </p>
              </div>
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">Select a template</p>
          )}
        </div>
      </div>

      {/* Create Template Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl m-4">
            <h2 className="text-xl font-bold mb-4">Create Template</h2>
            <form onSubmit={handleCreateTemplate} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Tenant</label>
                  <select name="tenantId" className="input">
                    <option value="_global">Global</option>
                    {tenants?.map((tenant) => (
                      <option key={tenant.tenantId} value={tenant.tenantId}>
                        {tenant.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="label">Label Type</label>
                  <select name="labelType" className="input" required>
                    <option value="shipping">Shipping</option>
                    <option value="carton">Carton</option>
                    <option value="pallet">Pallet</option>
                    <option value="product">Product</option>
                    <option value="custom">Custom</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="label">Industry</label>
                  <input name="industry" className="input" required placeholder="travel-center" />
                </div>
                <div>
                  <label className="label">Company
                    <InfoTooltip text="Must match a Retailer ID exactly (e.g., walmart, buc-ees). This links the template to the retailer." />
                  </label>
                  <input name="company" className="input" required placeholder="buc-ees" />
                </div>
                <div>
                  <label className="label">Template Name
                    <InfoTooltip text="Descriptive slug for the label type. Convention: shipping-4x6, carton-3x0.75. Combined with industry and company to form the template key." />
                  </label>
                  <input name="templateName" className="input" required placeholder="shipping-4x6" />
                </div>
              </div>
              <div>
                <label className="label">Description</label>
                <input name="description" className="input" placeholder="Buc-ee's Shipping Label" />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="label">Width (inches)</label>
                  <input name="width" type="number" step="0.1" className="input" required defaultValue="4" />
                </div>
                <div>
                  <label className="label">Height (inches)</label>
                  <input name="height" type="number" step="0.1" className="input" required defaultValue="6" />
                </div>
                <div>
                  <label className="label">DPI</label>
                  <select name="dpi" className="input">
                    <option value="203">203 DPI</option>
                    <option value="300">300 DPI</option>
                    <option value="600">600 DPI</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="label">ZPL Content
                  <InfoTooltip text="Use Handlebars syntax: {{variableName}} for fields, {{#if field}}...{{/if}} for conditionals, {{> partialName}} for reusable blocks." />
                </label>
                <textarea
                  name="zplContent"
                  className="input font-mono text-sm"
                  rows={12}
                  required
                  placeholder="^XA&#10;^FO50,50^A0N,30,30^FD{{companyName}}^FS&#10;^FO50,100^BY2^BCN,100,Y,N,N^FD{{barcode}}^FS&#10;^XZ"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Use Handlebars syntax for variables: {'{{variableName}}'}
                </p>
              </div>
              <div className="flex justify-end space-x-3">
                <button type="button" className="btn btn-secondary" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={createTemplateMutation.isPending}>
                  {createTemplateMutation.isPending ? 'Creating...' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Assign Template Modal */}
      {showAssignModal && selectedTemplate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Assign Template</h2>
            <p className="text-sm text-gray-600 mb-4">
              Assign <strong>{selectedTemplate.templateName || selectedTemplate.templateKey}</strong> to a tenant
            </p>
            <form onSubmit={handleAssignTemplate} className="space-y-4">
              <div>
                <label className="label">Tenant</label>
                <select name="assignTenantId" className="input" required>
                  <option value="">Select tenant...</option>
                  {tenants?.map((tenant) => (
                    <option key={tenant.tenantId} value={tenant.tenantId}>
                      {tenant.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label">Label Type Override (optional)</label>
                <select name="assignLabelType" className="input">
                  <option value={selectedTemplate.labelType}>
                    Same as template ({selectedTemplate.labelType})
                  </option>
                  <option value="shipping">Shipping</option>
                  <option value="carton">Carton</option>
                  <option value="pallet">Pallet</option>
                  <option value="product">Product</option>
                  <option value="custom">Custom</option>
                </select>
              </div>
              <div>
                <label className="label">Priority</label>
                <input
                  name="priority"
                  type="number"
                  className="input"
                  defaultValue="0"
                  placeholder="0 = default"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Higher priority templates are selected first
                </p>
              </div>
              <div className="flex justify-end space-x-3">
                <button type="button" className="btn btn-secondary" onClick={() => setShowAssignModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={assignTemplateMutation.isPending}>
                  {assignTemplateMutation.isPending ? 'Assigning...' : 'Assign'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirm Approve/Activate Dialog */}
      {confirmAction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setConfirmAction(null)}>
          <div className="bg-white rounded-lg p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-xl font-bold mb-4">
              {confirmAction.type === 'approve' ? 'Approve Template?' : 'Activate Template?'}
            </h2>
            <p className="text-gray-600 mb-6">
              {confirmAction.type === 'approve'
                ? `Approve template "${confirmAction.key}" version ${confirmAction.version}? This marks it as ready for activation.`
                : `Activate template "${confirmAction.key}" version ${confirmAction.version}? This will make it available for label generation.`}
            </p>
            {(approveTemplateMutation.isError || activateTemplateMutation.isError) && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                {(approveTemplateMutation.error || activateTemplateMutation.error) instanceof Error
                  ? (approveTemplateMutation.error || activateTemplateMutation.error)?.message
                  : 'Action failed'}
              </div>
            )}
            <div className="flex justify-end space-x-3">
              <button className="btn btn-secondary" onClick={() => setConfirmAction(null)}>Cancel</button>
              <button
                className={`px-4 py-2 text-sm font-medium text-white rounded-lg ${
                  confirmAction.type === 'approve'
                    ? 'bg-blue-600 hover:bg-blue-700'
                    : 'bg-green-600 hover:bg-green-700'
                }`}
                onClick={() => {
                  if (confirmAction.type === 'approve') {
                    approveTemplateMutation.mutate({ key: confirmAction.key, version: confirmAction.version });
                  } else {
                    activateTemplateMutation.mutate({ key: confirmAction.key, version: confirmAction.version });
                  }
                }}
                disabled={approveTemplateMutation.isPending || activateTemplateMutation.isPending}
              >
                {(approveTemplateMutation.isPending || activateTemplateMutation.isPending)
                  ? (confirmAction.type === 'approve' ? 'Approving...' : 'Activating...')
                  : (confirmAction.type === 'approve' ? 'Approve' : 'Activate')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
